import React, { useCallback, useRef, useState, useEffect } from 'react';
import styled from 'astroturf/react';

import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';
import {
  trackClick,
  Options as ClickTrackingOptions,
} from 'js/utils/trackClick';
import { consoleError } from 'js/utils/console';

import { Button } from 'js/design_system/Button';
import { Input } from 'js/design_system/forms/Input';

import { TitleText, BodyText } from './common';
import { BackButton } from '../components/BackButton';
import { CenteredDiv } from '../components/CenteredDiv';
import { VerticalSpacer } from '../components/VerticalSpacer';
import { ModalError } from '../components/ModalError';
import {
  EmailRegistrationPageConfigurableText,
  RegistrationParams,
  ModalPage,
} from '../types';

const FlexDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  width: 100%;

  & > * {
    flex: 1;
  }
`;

type Props = {
  userEmail: string;
  onSuccess: (redirect?: string) => void;
  setPage: React.Dispatch<React.SetStateAction<ModalPage>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  show: boolean;
  clickTrackingContext?: Partial<ClickTrackingOptions>;
  text: EmailRegistrationPageConfigurableText;
  registrationParams: RegistrationParams;
};
export const EmailRegistrationPage: React.FC<Props> = (props) => {
  const {
    userEmail,
    onSuccess,
    setPage,
    show,
    setLoading,
    clickTrackingContext,
    text,
    registrationParams,
  } = props;
  const [errorOccurred, setErrorOccurred] = useState(false);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const [firstNameErrorText, setFirstNameErrorText] = useState<string>('');
  const [lastNameErrorText, setLastNameErrorText] = useState<string>('');
  const [passwordErrorText, setPasswordErrorText] = useState<string>('');

  const clearAllErrors = useCallback(() => {
    setErrorOccurred(false);
    setFirstNameErrorText('');
    setLastNameErrorText('');
    setPasswordErrorText('');
  }, []);

  const handleSubmit = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      clearAllErrors();
      setLoading(true);
      trackClick({
        source: 'login_modal_email_registration_submit_button',
        ...clickTrackingContext,
      });
      const resp = await fetchWithCsrf(
        routes.api_v0_register_path({ subdomain_param: 'api' }),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user: {
              first_name: firstNameRef.current?.value,
              last_name: lastNameRef.current?.value,
              email: userEmail,
              password: passwordRef.current?.value,
            },
            terms_shown: true,
            ...registrationParams,
          }),
        },
      );
      try {
        const respBody = await resp.json();

        if (respBody.success) {
          onSuccess(respBody.redirect);
        } else {
          consoleError(respBody);
          setLoading(false);

          let specificErrorFound = false;
          if (respBody.errors['user[first_name]']) {
            setFirstNameErrorText(respBody.errors['user[first_name]']);
            specificErrorFound = true;
          }
          if (respBody.errors['user[last_name]']) {
            setLastNameErrorText(respBody.errors['user[last_name]']);
            specificErrorFound = true;
          }
          if (respBody.errors['user[password]']) {
            setPasswordErrorText(respBody.errors['user[password]']);
            specificErrorFound = true;
          }
          if (!specificErrorFound) {
            setErrorOccurred(true);
          }
        }
      } catch (e) {
        consoleError(e as Error);
        setLoading(false);
        setErrorOccurred(true);
      }
    },
    [
      clearAllErrors,
      registrationParams,
      onSuccess,
      setLoading,
      userEmail,
      clickTrackingContext,
    ],
  );

  useEffect(() => {
    if (show && firstNameRef.current) {
      firstNameRef.current.focus();
    }
  }, [show]);

  return (
    <div style={{ display: show ? 'initial' : 'none' }}>
      <BackButton
        onClick={() => {
          trackClick({
            source: 'login_modal_email_registration_back_button',
            ...clickTrackingContext,
          });
          setPage('email-entry');
        }}
      />
      <TitleText>{text.header}</TitleText>
      <BodyText>{text.subheader}</BodyText>
      <ModalError show={errorOccurred} />
      <VerticalSpacer height={16} />
      <CenteredDiv>
        <BodyText
          style={{ fontSize: '12px', textAlign: 'center', color: '#666' }}
        >
          By clicking {text.submit}, you agree to our{' '}
          <a href="/terms" style={{ textDecoration: 'underline' }}>
            Terms of Use
          </a>{' '}
          and{' '}
          <a href="/privacy" style={{ textDecoration: 'underline' }}>
            Privacy Policy
          </a>
        </BodyText>
      </CenteredDiv>
      <VerticalSpacer height={16} />
      <FlexDiv>
        <Input
          label="First name"
          name="given-name"
          ref={firstNameRef}
          type="text"
          autoComplete="given-name"
          placeholder="First name"
          required={show}
          hasError={!!firstNameErrorText}
          errors={firstNameErrorText ? [firstNameErrorText] : []}
        />
        <Input
          label="Last name"
          name={'family-name'}
          ref={lastNameRef}
          autoComplete="family-name"
          placeholder="Last name"
          required={show}
          hasError={!!lastNameErrorText}
          errors={lastNameErrorText ? [lastNameErrorText] : []}
        />
      </FlexDiv>
      <VerticalSpacer height={16} />
      <Input
        label="Password"
        name={'new-password'}
        ref={passwordRef}
        type="password"
        autoComplete="new-password"
        placeholder="Create a password"
        required={show}
        hasError={!!passwordErrorText}
        errors={passwordErrorText ? [passwordErrorText] : []}
      />
      <VerticalSpacer height={40} />
      <CenteredDiv>
        <Button
          size="lg"
          type="submit"
          disabled={!show}
          iconRight="arrow_forward"
          onClick={handleSubmit}
        >
          {text.submit}
        </Button>
      </CenteredDiv>
    </div>
  );
};
